'use client';

import { Film } from '@/app/[festival-slug]/[locale]/(rest)/films/[slug]/page';
import Image from '@/components/Image';
import ThemedIcon from '@/components/ui/ThemedIcon';
import { Link } from '@/i18n/navigation';
import { QuickFilter as MappedStaticFilter } from '@/utils/quick-filters';
import { cn } from '@/utils/tailwind-merge';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Swiper as SwiperCore } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import BlogCard from './BlogCard';
import FilmCard, { FilmListItem } from './FilmCard';
import { Card } from './Tabs/Tabs';

export interface Slide {
    imageSrc: string;
    imageAlt: string;
    date: string;
    time: string;
    location: string;
    title: {
        en: string;
        fr: string;
    };
    description: {
        en: string;
        fr: string;
    };
    action?: {
        label: string;
        link?: string;
        onClick?: () => void;
    };
}

interface CarouselProps {
    slides: Slide[] | FilmListItem[] | Film[] | Card[] | string[];
    slidesPerView?: number;
    filmsSlider?: boolean;
    photoSlider?: boolean;
    cardsSlider?: boolean;
    forceSrc?: boolean;
    mappedStaticFilters?: MappedStaticFilter[];
}

const SlideNextButton = ({ onClick, className }: { onClick: () => void; className?: string }) => (
    <button
        className={cn(
            'absolute right-1/3 top-0 bottom-0 m-auto text-9xl text-primary z-10',
            className,
        )}
        onClick={onClick}
        aria-label="Next slide"
    >
        <ThemedIcon
            className="w-9 h-9 text-primary"
            src="/img/icons/arrow.svg"
            width={50}
            height={50}
        />
    </button>
);

const SlidePrevButton = ({ onClick, className }: { onClick: () => void; className?: string }) => (
    <button
        className={cn(
            'absolute -left-4 top-0 bottom-0 m-auto text-9xl text-primary z-10',
            className,
        )}
        onClick={onClick}
        aria-label="Previous slide"
    >
        <ThemedIcon
            className="w-9 h-9 transform rotate-180 text-primary"
            src="/img/icons/arrow.svg"
            width={50}
            height={50}
        />
    </button>
);

const SlidePagination = ({
    slidesLength,
    classNames,
}: {
    slidesLength: number;
    classNames?: string;
}) => {
    const swiper = useSwiper();
    const [currentSlideIndex, setCurrentSlideIndex] = useState(swiper.activeIndex);

    useEffect(() => {
        swiper.on('slideChange', () => {
            setCurrentSlideIndex(swiper.activeIndex);
        });
    });

    return (
        <div
            className={cn(
                'absolute right-[calc(33.3%-108px)] bottom-0 w-fit flex items-center gap-2 z-20',
                classNames,
            )}
        >
            {Array.from({ length: slidesLength }, (_, index) => (
                <button
                    key={index}
                    type="button"
                    className={`w-4 h-4 ${currentSlideIndex === index ? 'bg-primary' : 'bg-gray-200'}`}
                    onClick={() => swiper.slideTo(index)}
                    aria-label={`Go to slide ${index + 1}`}
                    aria-current={currentSlideIndex === index ? 'true' : undefined}
                />
            ))}
        </div>
    );
};

const Carousel = ({
    slides,
    filmsSlider = false,
    photoSlider = false,
    cardsSlider = false,
    slidesPerView = 1,
    forceSrc = false,
    mappedStaticFilters,
}: CarouselProps) => {
    const t = useTranslations();
    const swiperRef = useRef<SwiperCore | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const prevButtonRef = useRef<HTMLDivElement>(null);
    const nextButtonRef = useRef<HTMLDivElement>(null);
    const locale = useLocale() as 'en' | 'fr';

    const handlePrevClick = () => swiperRef.current?.slidePrev();
    const handleNextClick = () => swiperRef.current?.slideNext();

    useEffect(() => {
        if (containerRef.current && prevButtonRef.current && nextButtonRef.current) {
            containerRef.current.appendChild(prevButtonRef.current);
            containerRef.current.appendChild(nextButtonRef.current);
        }
    }, []);

    return (
        <div
            className={`relative ${filmsSlider && 'films-slider'} ${photoSlider && 'photo-slider'}`}
            ref={containerRef}
        >
            <Swiper
                slidesPerView={slidesPerView}
                onSwiper={(swiperInstance) => {
                    swiperRef.current = swiperInstance;
                }}
                spaceBetween={25}
                watchSlidesProgress
            >
                {slides.map((slide, index) => (
                    <SwiperSlide key={index}>
                        {isSlide(slide) && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-[60px]">
                                <div className="relative w-full col-span-2">
                                    <Image
                                        src={slide.imageSrc}
                                        alt={slide.imageAlt}
                                        width={1920}
                                        height={1080}
                                        className="w-full h-full object-cover"
                                        forceSrc={forceSrc}
                                    />
                                    {slide.date && slide.time && (
                                        <div className="absolute left-0 bottom-5 md:bottom-16 px-4 py-2.5 flex flex-col uppercase text-[22px] md:text-[28px] leading-8 bg-white text-black font-trade-gothic font-normal">
                                            <span>
                                                {slide.date} {slide.time}
                                            </span>
                                            <span>{slide.location}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="h-full flex flex-col max-md:gap-4 col-span-1">
                                    <span
                                        className="text-4xl 2xl:text-6xl font-bold uppercase"
                                        dangerouslySetInnerHTML={{ __html: slide.title[locale] }}
                                    />
                                    <div className="flex-auto flex flex-col justify-end pb-10 2xl:pb-14">
                                        <span
                                            className="text-xl 2xl:text-[26px] text-gray-200 font-light 2xl:leading-8 tracking-wide font-helvetica-neue"
                                            dangerouslySetInnerHTML={{
                                                __html: slide.description[locale],
                                            }}
                                        />
                                    </div>
                                    {slide.action && slide.action.link && (
                                        <Link
                                            href={slide.action.link}
                                            className="text-black bg-primary px-5 sm:px-10 lg:px-16 py-2 w-fit uppercase text-2xl hover:opacity-95"
                                        >
                                            {t(slide.action.label)}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )}
                        {filmsSlider && (
                            <FilmCard
                                key={(slide as FilmListItem).id}
                                {...(slide as FilmListItem)}
                                mappedStaticFilters={mappedStaticFilters}
                            />
                        )}
                        {cardsSlider && (
                            <div className="relative pb-12">
                                <BlogCard key={(slide as Card).id} {...(slide as Card)} />
                            </div>
                        )}
                        {photoSlider && typeof slide === 'string' && (
                            <div className="w-full lg:h-[630px] h-[210px] relative">
                                <Image
                                    className="absolute top-0 left-0 w-full h-full object-cover object-center"
                                    src={slide}
                                    alt="Image"
                                    key={slide}
                                    fill
                                    forceSrc={forceSrc}
                                />
                            </div>
                        )}
                    </SwiperSlide>
                ))}
                {(filmsSlider || photoSlider || cardsSlider) && (
                    <SlidePagination slidesLength={slides.length} classNames="left-0 bottom-0" />
                )}
            </Swiper>
            {(filmsSlider || photoSlider) && (
                <>
                    <div ref={prevButtonRef}>
                        <SlidePrevButton
                            onClick={handlePrevClick}
                            className={cn(
                                '-left-[2%] -top-1/2 max-lg:hidden',
                                photoSlider && 'top-0',
                            )}
                        />
                    </div>
                    <div ref={nextButtonRef}>
                        <SlideNextButton
                            onClick={handleNextClick}
                            className={cn(
                                'right-[3%] -top-1/2 max-lg:hidden',
                                photoSlider && 'top-0',
                            )}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default Carousel;

function isSlide(slide: Slide | FilmListItem | Film | Card | string): slide is Slide {
    return (slide as Slide).imageSrc !== undefined;
}
